import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateContactQueries } from './useContact';

/**
 * Updates an existing structured key date (birthday / policy / customer reference) in place.
 * Mirrors {@link useContactKeyDateCreateMutation} — same body + response envelope — but PUTs to
 * `contact/{id}/keyfacts/{keyFactId}/keyDate` (the key-fact-scoped route, matching the delete
 * mutation's URL shape). The server rebuilds the key fact's value/keyDate from the supplied
 * metadata and preserves the key fact's id.
 */
export function useContactKeyDateUpdateMutation({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (result: Api.IContact) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			contactId,
			keyFactId,
			keyFactMetaData,
			ignoreYear = false,
		}: {
			contactId: string;
			keyFactId: string;
			keyFactMetaData: Api.IKeyFactMetaData;
			ignoreYear?: boolean;
		}) => {
			return userSession.webServiceHelper.callAsync<Api.IContact>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `contact/${encodeURIComponent(contactId)}/keyfacts/${encodeURIComponent(keyFactId)}/keyDate`,
					queryParams: {
						ignoreYear,
					},
				}),
				'PUT',
				keyFactMetaData
			);
		},
		onSuccess: result => {
			invalidateContactQueries(queryClient, result.id);
			onSuccess?.(result);
		},
		onError,
	});
}
